import React, { useEffect, useState } from 'react';
import { TRANSFORMERS } from '@lexical/markdown';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_CRITICAL,
  INSERT_PARAGRAPH_COMMAND,
  KEY_ENTER_COMMAND,
} from 'lexical';

import AutoLinkPlugin from '../plugins/AutoLinkPlugin';
import ToolbarPlugin from '../plugins/ToolbarPlugin';
import TreeViewPlugin from '../plugins/TreeViewPlugin';
import ImagePlugin from '../plugins/ImagePlugin';

import DragDropPastePlugin from '../plugins/DragDropPastePlugin';
import InitialEditorStatePlugin from '../plugins/InitialEditorStatePlugin';
import FloatingLinkEditorPlugin from '../plugins/FloatingLinkEditorPlugin';
import HorizontalRulePlugin from '../plugins/HorizontalRulePlugin';

import { isDevelopment } from '@dashboard/services/env';
import CollapsiblePlugin from '../plugins/CollapsiblePlugin';
import HtmlPlugin from '../plugins/HtmlPlugin';
import { AutoFocusPlugin } from '../plugins/AutoFocusPlugin';

import BaseEditor from './BaseEditor';

function Placeholder({ placeholder }) {
  return (
    <div className="overflow-hidden absolute truncate top-4 left-2 select-none inline-block pointer-events-none text-gray-500">
      {placeholder}
    </div>
  );
}

const CAN_USE_DOM =
  typeof window !== 'undefined' &&
  typeof window.document !== 'undefined' &&
  typeof window.document.createElement !== 'undefined';
const documentMode = CAN_USE_DOM && 'documentMode' in document ? document.documentMode : null;
const IS_SAFARI = CAN_USE_DOM && /Version\/[\d.]+.*Safari/.test(navigator.userAgent);
const IS_IOS = CAN_USE_DOM && /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
const IS_CHROME = CAN_USE_DOM && /^(?=.*Chrome).*/i.test(navigator.userAgent);
const IS_APPLE_WEBKIT =
  CAN_USE_DOM && /AppleWebKit\/[\d.]+/.test(navigator.userAgent) && !IS_CHROME;
const CAN_USE_BEFORE_INPUT =
  CAN_USE_DOM && 'InputEvent' in window && !documentMode
    ? 'getTargetRanges' in new window.InputEvent('input')
    : false;

function RichTextEditor({ onChange, value, placeholder, onLoading }) {
  const [isLinkEditMode, setIsLinkEditMode] = useState(false);
  const [editor] = useLexicalComposerContext();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    editor.update(() => {
      // insert paragraph instead of line break on SHIFT+ENTER
      editor.registerCommand(
        KEY_ENTER_COMMAND,
        event => {
          const selection = $getSelection();
          if (!$isRangeSelection(selection)) {
            return false;
          }

          if (event !== null) {
            // If we have beforeinput, then we can avoid blocking
            // the default behavior. This ensures that the iOS can
            // intercept that we're actually inserting a paragraph,
            // and autocomplete, autocapitalize etc work as intended.
            // This can also cause a strange performance issue in
            // Safari, where there is a noticeable pause due to
            // preventing the key down of enter.
            if ((IS_IOS || IS_SAFARI || IS_APPLE_WEBKIT) && CAN_USE_BEFORE_INPUT) {
              return false;
            }

            event.preventDefault();

            if (event.shiftKey) {
              return editor.dispatchCommand(INSERT_PARAGRAPH_COMMAND, undefined);
            }
          }
          return editor.dispatchCommand(INSERT_PARAGRAPH_COMMAND, undefined);
        },
        COMMAND_PRIORITY_CRITICAL,
      );
    });
  }, [editor]);

  useEffect(() => {
    onLoading(isLoading);
  }, [isLoading]);

  // we need an element reference for the container element that
  // any floating elements in plugins will be rendered inside
  const [floatingAnchorElem, setFloatingAnchorElem] = React.useState(null);
  const onContentEditableRef = _floatingAnchorElem => {
    if (_floatingAnchorElem !== null) {
      setFloatingAnchorElem(_floatingAnchorElem);
    }
  };

  return (
    <>
      <div className="editor-container rounded-sm text-black relative leading-5 text-left">
        <div className="sticky top-16 z-10">
          <ToolbarPlugin setIsLinkEditMode={setIsLinkEditMode} setIsLoading={setIsLoading} />
        </div>

        <div className="editor-inner bg-white relative rounded shadow-input">
          <InitialEditorStatePlugin initialValue={value} />

          <RichTextPlugin
            contentEditable={
              <div ref={onContentEditableRef}>
                <ContentEditable className="editor-input min-h-[150px] resize-none caret-black relative outline-none py-4 px-2" />
              </div>
            }
            placeholder={<Placeholder placeholder={placeholder} />}
            ErrorBoundary={LexicalErrorBoundary}
          />
          <DragDropPastePlugin setIsLoading={setIsLoading} />
          <ImagePlugin />
          <HistoryPlugin />
          {isDevelopment && <TreeViewPlugin />}
          <ListPlugin />
          <LinkPlugin />

          {floatingAnchorElem !== null && (
            <FloatingLinkEditorPlugin
              anchorElem={floatingAnchorElem}
              isLinkEditMode={isLinkEditMode}
              setIsLinkEditMode={setIsLinkEditMode}
            />
          )}

          <HorizontalRulePlugin />
          <CollapsiblePlugin />
          <HtmlPlugin />

          {/* <ClickableLinkPlugin /> */}
          <AutoLinkPlugin />
          <MarkdownShortcutPlugin transformers={TRANSFORMERS} />
          <OnChangePlugin onChange={onChange} />
          <AutoFocusPlugin />
        </div>
      </div>
    </>
  );
}

export default function RichTextEditorWrapper({ value, placeholder, onChange, onLoading }) {
  // Update the editor state
  function handleOnEditorChange(editorState) {
    // Call toJSON on the EditorState object, which produces a serialization safe string
    const editorStateJSON = editorState.toJSON();
    // However, we still have a JavaScript object, so we need to convert it to an actual string with JSON.stringify
    onChange(JSON.stringify(editorStateJSON));
  }

  return (
    <BaseEditor value={value}>
      <RichTextEditor
        value={value}
        placeholder={placeholder}
        onChange={handleOnEditorChange}
        onLoading={onLoading}
      />
    </BaseEditor>
  );
}
